import * as React from 'react';
import { values } from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { State } from '../../redux/state';
import { asPage } from '../../utils/wrappers/asPage';
import { LProps, withLocalization } from '../../utils/wrappers';
import Footer from '../../shared/Footer';
import {
  computeCatalogBreakdown,
  filterCatalogPledges
} from '../../utils/catalogBreakdown';
import { makeSimpleRoutePath, Routes } from '../../utils/routerHelper';

interface Props {
  breakdown: ReturnType<typeof computeCatalogBreakdown>;
}

const CatalogBreakdownPage = ({ t, formatNumber, breakdown }: LProps<Props>) => (
  <div className="cc__page__column_content">
    <div style={{ width: '100%' }}>
      <div className="cc__white_box">
        <h2>{t('Challenge catalog points breakdown')}</h2>
        <p className="cc__lighttext">
          {t('Summary of active actions in this challenge, grouped by unlock level and section.')}
        </p>
        <p className="cc__lighttext">
          {t('Max points per action = baseline points + min(baseline, 50) × (completions allowed − 1). Photo bonus points are not included.')}
        </p>
        <p>
          <Link to={makeSimpleRoutePath(Routes.Pledges, t)}>{t('Back to actions')}</Link>
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', margin: '20px 0' }}>
          <div className="cc__stats__item">
            <div className="cc__stats__label">{t('Actions')}</div>
            <div className="cc__lighttext">{formatNumber(breakdown.totalActions, 0)}</div>
          </div>
          <div className="cc__stats__item">
            <div className="cc__stats__label">{t('Max completions')}</div>
            <div className="cc__lighttext">{formatNumber(breakdown.totalMaxCompletions, 0)}</div>
          </div>
          <div className="cc__stats__item">
            <div className="cc__stats__label">{t('Max points')}</div>
            <div className="cc__lighttext">{formatNumber(breakdown.totalMaxPoints, 0)}</div>
          </div>
        </div>

        <h3>{t('By level — with cumulative totals')}</h3>
        <div style={{ overflowX: 'auto', marginBottom: '24px' }}>
          <table className="table table-striped table-sm">
            <thead>
              <tr>
                <th>{t('Level')}</th>
                <th className="text-right">{t('Unlock at')}</th>
                <th className="text-right">{t('Actions')}</th>
                <th className="text-right">{t('Max completions')}</th>
                <th className="text-right">{t('Max pts (level)')}</th>
                <th className="text-right">{t('Cumulative actions')}</th>
                <th className="text-right">{t('Cumulative max pts')}</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.byLevel.map((row) =>
                <tr key={row.scoreLevelName}>
                  <td>{row.scoreLevelName}</td>
                  <td className="text-right">{formatNumber(row.threshold, 0)}</td>
                  <td className="text-right">{formatNumber(row.actionCount, 0)}</td>
                  <td className="text-right">{formatNumber(row.maxCompletions, 0)}</td>
                  <td className="text-right">{formatNumber(row.maxPoints, 0)}</td>
                  <td className="text-right">{formatNumber(row.cumulativeActions, 0)}</td>
                  <td className="text-right">{formatNumber(row.cumulativeMaxPoints, 0)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <h3>{t('By section — section totals')}</h3>
        <div style={{ overflowX: 'auto', marginBottom: '24px' }}>
          <table className="table table-striped table-sm">
            <thead>
              <tr>
                <th>{t('Section')}</th>
                <th className="text-right">{t('Actions')}</th>
                <th className="text-right">{t('Max completions')}</th>
                <th className="text-right">{t('Max pts')}</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.bySection.map((row) =>
                <tr key={row.sectionName}>
                  <td>{row.sectionName}</td>
                  <td className="text-right">{formatNumber(row.actionCount, 0)}</td>
                  <td className="text-right">{formatNumber(row.maxCompletions, 0)}</td>
                  <td className="text-right">{formatNumber(row.maxPoints, 0)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <h3>{t('Section × level — action counts')}</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-striped table-sm">
            <thead>
              <tr>
                <th>{t('Section')}</th>
                {breakdown.scoreLevelNames.map((name) =>
                  <th key={name} className="text-right">{name}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {breakdown.sectionByLevel.map((row) =>
                <tr key={row.sectionName}>
                  <td>{row.sectionName}</td>
                  {row.levelCounts.map((count, index) =>
                    <td key={`${row.sectionName}-${index}`} className="text-right">
                      {formatNumber(count, 0)}
                    </td>
                  )}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  </div>
);

const mapStateToProps = (state: State): Props => {
  const pledges = filterCatalogPledges(
    values(state.competitionPledges || {}),
    state.competition,
    state.user
  );
  return {
    breakdown: computeCatalogBreakdown(
      pledges,
      state.competitionScoreLevels || [],
      state.competitionPledgeCategories || {}
    )
  };
};

export default asPage({
  isGrey: true,
  requiresCompetitionStart: true,
  requiresCompetitionVerification: true
})(compose(
  withLocalization('pledges'),
  connect(mapStateToProps)
)(CatalogBreakdownPage));
